/**
 * 4. Workspace Creation — creates the tenant: company name, workspace code,
 * country, currency, time zone and language. Provisioning then installs the
 * country's pack, and keeps the currency, time zone and language chosen here
 * (D19).
 *
 * D25: this screen used to send USD, UTC and en-US whatever the creator
 * wanted, with no way to choose, and "US" as the country whenever the
 * country list had not loaded. Nothing is sent now that the creator did not
 * pick: every picker starts empty, and the form is not sent until each one
 * is chosen. When the chosen country's pack says which currency, time zone
 * and language it uses, those are filled in as suggestions; one the creator
 * picked by hand is never replaced. (The pack list in the API does not carry
 * them yet, so today nothing is suggested.)
 *
 * The industry, company-size and "sample data" choices are gone: none of
 * them was sent anywhere or did anything, and a choice that changes nothing
 * is a dead control.
 *
 * After the workspace is created the person signs in again. The token in
 * hand was issued before the workspace existed: it names no workspace (a
 * first-time owner) or another one, and the API has no way to exchange it.
 * Carrying on with it showed the setup wizard another workspace's settings
 * under the new code, or a 401. For a first-time owner, signing in again
 * issues a token for the new workspace, and the sign-in page says why it is
 * asked for. Sign-in opens the first of a person's workspaces by name and
 * offers no choice yet (D50), so someone who already had a workspace is told
 * that instead of being promised the new one.
 *
 * Sign out is offered here too: a person who signed in with the wrong
 * account, or who cannot create a workspace right now, is not kept on this
 * page.
 *
 * W2.8b, option B (owner decision 1 on Decision 89): the creator is the new
 * workspace's owner, and an owner needs two-step sign-in. When the API
 * answers `mfaEnrollmentRequired` with an enrolment token, the workspace is
 * created but no session is opened; this page then shows the set-up step
 * (FactorEnrolment: the setup key and the recovery codes, once, then a code
 * from the app), and confirming answers the session for the NEW workspace,
 * which this page adopts and opens. Create, enrol, inside: the sign-in page
 * is never shown on the way. The sign-in route above remains only for an
 * answer that carries no enrolment token (enforcement turned off).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  PageHeader,
} from "@/components/ui";
import { ApiError, post } from "@/lib/api/client";
import { FactorEnrolment, type Enrolled } from "./FactorEnrolment";
import { SESSION_MESSAGES } from "@/lib/auth/messages";
import { SIGN_OUT_TIMEOUT_MS, signOut } from "@/lib/auth/sign-out";
import { useAuthStore } from "@/lib/auth/store";
import { errorMessage, useTranslation } from "@/lib/i18n";
import {
  CURRENCY_OPTIONS,
  LANGUAGE_OPTIONS,
  NOT_A_BOOKS_CURRENCY,
  TIMEZONE_OPTIONS,
  currencyName,
  languageName,
  useCountryOptions,
  type CountryOption,
} from "@/lib/reference";

interface ProvisionResponse {
  id: string;
  status: string;
  provisioningJobId: string;
  /**
   * The new workspace's session, opened by the creation (its `sid` names a
   * "UserSession" row). This page does not use it to sign in; it only ends
   * it (endCreatedSession).
   */
  accessToken?: string;
  /** W2.8b option B: set up two-step sign-in first, with this token. */
  mfaEnrollmentRequired?: boolean;
  enrollmentToken?: string;
}

/**
 * End, on the server, the session POST /v1/tenants opened (D1041).
 *
 * The forced sign-out below used to send only the token this page held,
 * which names no session (a first-time owner) or another one. The session
 * the creation opened was never named, so it stayed live for its 12-hour
 * idle limit and was listed under Active sessions after the next sign-in,
 * with its token still accepted. Its refresh token was never delivered (POST
 * /v1/tenants sets no cookie), so its access token is the only thing that can
 * name it, and it is sent here, to the public logout route only.
 *
 * Like signOut, a failure never keeps anyone signed in: the local sign-out
 * follows whatever this returns. The token is not kept anywhere.
 */
async function endCreatedSession(token: string | undefined): Promise<void> {
  if (!token) return;
  try {
    await post<unknown>("/auth/logout", undefined, undefined, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: SIGN_OUT_TIMEOUT_MS,
    });
  } catch {
    // Deliberately ignored, as in signOut: the sign-out is what was asked.
  }
}

/** The rules POST /v1/tenants enforces (tenant-provisioning.service.ts). */
const NAME_LENGTH = { min: 2, max: 200 };
const CODE_LENGTH = { min: 2, max: 60 };
const CODE_PATTERN = /^[a-z0-9-]+$/;

/** A picker's value, and whether the creator chose it rather than the pack. */
interface Pick {
  value: string;
  byHand: boolean;
}
const UNPICKED: Pick = { value: "", byHand: false };

type FieldName =
  "name" | "code" | "country" | "currency" | "timezone" | "language";

/** Ids of the controls, so the first invalid one can take focus. */
const IDS: Record<FieldName, string> = {
  name: "workspace-name",
  code: "workspace-code",
  country: "workspace-country",
  currency: "workspace-currency",
  timezone: "workspace-timezone",
  language: "workspace-language",
};
const ORDER: FieldName[] = [
  "name",
  "code",
  "country",
  "currency",
  "timezone",
  "language",
];

const TIMEZONES = new Set(TIMEZONE_OPTIONS.map((tz) => tz.value));

/** A currency a pack may suggest: an ISO 4217 code books can be kept in. */
function booksCurrency(code: string): boolean {
  return /^[A-Z]{3}$/.test(code) && !NOT_A_BOOKS_CURRENCY.has(code);
}

function canonicalLocale(tag: string | undefined): string | undefined {
  if (!tag) return undefined;
  try {
    return Intl.getCanonicalLocales(tag)[0];
  } catch {
    return undefined;
  }
}

/**
 * What the pack suggests for one picker, if it suggests a value the picker
 * offers. A picker the creator set by hand keeps their choice.
 */
function suggest(
  current: Pick,
  suggestion: string | undefined,
  offered: (value: string) => boolean,
): Pick {
  if (current.byHand) return current;
  return suggestion && offered(suggestion)
    ? { value: suggestion, byHand: false }
    : UNPICKED;
}

export function WorkspaceCreationPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const sessionStatus = useAuthStore((s) => s.sessionStatus);
  // W2.8b option B: the workspace exists; its owner sets up two-step sign-in
  // with this token, and confirming opens it.
  const [enrolFor, setEnrolFor] = useState<string | null>(null);

  // Creating a workspace is an authenticated call — the owner is taken from
  // the token's subject, never from the form. Reaching this page without a
  // session (a bookmark, a reload after the token expired) would otherwise
  // only fail at submit time, as a raw 401 on an already-filled-in form.
  // While the page is still asking the server for its session (a reload),
  // it waits for the answer instead of leaving. A token issued before any
  // workspace exists has no refresh, so a reload then does ask for sign-in
  // (decision #53).
  useEffect(() => {
    if (sessionStatus !== "restoring" && !accessToken && !enrolFor)
      navigate("/login", { replace: true });
  }, [accessToken, sessionStatus, navigate, enrolFor]);

  const countries = useCountryOptions();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState<Pick>(UNPICKED);
  const [timezone, setTimezone] = useState<Pick>(UNPICKED);
  const [locale, setLocale] = useState<Pick>(UNPICKED);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  // One idempotency key per distinct request, so sending the same form again
  // after a lost response replays it instead of creating a second workspace.
  const attempt = useRef<{ body: string; key: string } | null>(null);

  const chosenCountry: CountryOption | undefined = countries.options.find(
    (c) => c.code === country,
  );
  const packLocale = canonicalLocale(chosenCountry?.locale);
  const packCurrency = chosenCountry?.currencyCode;
  // The pack's currency is offered even where the runtime does not list it;
  // the one chosen always is.
  const currencies = useMemo(
    () =>
      [
        ...new Set(
          [...CURRENCY_OPTIONS, packCurrency, currency.value].filter(
            (code): code is string =>
              typeof code === "string" && booksCurrency(code),
          ),
        ),
      ].sort(),
    [packCurrency, currency.value],
  );
  // The pack's own locale, and whatever is chosen now, are always offered:
  // a select whose value is not among its options shows another option as
  // chosen while the form would send this one.
  const languages = useMemo(
    () => [
      ...new Set(
        [...LANGUAGE_OPTIONS, packLocale, locale.value].filter(
          (tag): tag is string => Boolean(tag),
        ),
      ),
    ],
    [packLocale, locale.value],
  );

  const chooseCountry = (next: string) => {
    setCountry(next);
    const pack = countries.options.find((c) => c.code === next);
    setCurrency((c) => suggest(c, pack?.currencyCode, booksCurrency));
    setTimezone((tz) => suggest(tz, pack?.timezone, (v) => TIMEZONES.has(v)));
    setLocale((l) =>
      suggest(l, canonicalLocale(pack?.locale), (v) => Boolean(v)),
    );
    setErrors((e) => ({ ...e, country: undefined }));
  };

  const validate = (): Partial<Record<FieldName, string>> => {
    const found: Partial<Record<FieldName, string>> = {};
    const trimmed = name.trim();
    if (trimmed.length < NAME_LENGTH.min || trimmed.length > NAME_LENGTH.max)
      found.name = t("auth.workspace.errors.name", NAME_LENGTH);
    if (
      !CODE_PATTERN.test(code) ||
      code.length < CODE_LENGTH.min ||
      code.length > CODE_LENGTH.max
    )
      found.code = t("auth.workspace.errors.code", CODE_LENGTH);
    if (!country) found.country = t("auth.workspace.errors.country");
    if (!currency.value) found.currency = t("auth.workspace.errors.currency");
    if (!timezone.value) found.timezone = t("auth.workspace.errors.timezone");
    if (!locale.value) found.language = t("auth.workspace.errors.language");
    return found;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const found = validate();
    setErrors(found);
    const firstInvalid = ORDER.find((f) => found[f]);
    if (firstInvalid) {
      document.getElementById(IDS[firstInvalid])?.focus();
      return;
    }

    // Exactly what the creator chose. Plan, isolation mode and data residency
    // are not the creator's choice and are not claimed on their behalf; the
    // owner is the token's subject, not a field.
    const body = {
      name: name.trim(),
      code,
      defaultCountryCode: country,
      defaultCurrencyCode: currency.value,
      timezone: timezone.value,
      locale: locale.value,
    };
    const serialized = JSON.stringify(body);
    if (attempt.current?.body !== serialized)
      attempt.current = { body: serialized, key: crypto.randomUUID() };

    setLoading(true);
    let created: ProvisionResponse;
    try {
      created = await post<ProvisionResponse>(
        "/tenants",
        body,
        attempt.current.key,
      );
    } catch (err) {
      // 409 is the only conflict this route returns: the code is taken.
      if (err instanceof ApiError && err.status === 409) {
        setErrors({ code: t("auth.workspace.errors.codeTaken") });
        document.getElementById(IDS.code)?.focus();
      } else {
        setSubmitError(errorMessage(err, t, language));
      }
      setLoading(false);
      return;
    }
    if (created.mfaEnrollmentRequired && created.enrollmentToken) {
      // Option B: set up two-step sign-in now; confirming opens the new
      // workspace. Nothing else is asked of the token in hand.
      setEnrolFor(created.enrollmentToken);
      setLoading(false);
      return;
    }
    // Created. This token cannot open it (see the header), so the session
    // ends here with a notice. For a first-time owner the next sign-in opens
    // the new workspace, their only one. Someone who already had a workspace
    // is told the truth instead: sign-in opens the first of theirs by name,
    // with no choice yet (D50).
    //
    // Two sessions end here: the one the creation opened (named only by the
    // token in its answer, D1041), and the one this page held, with the
    // refresh cookie the browser sends (signOut).
    const hadWorkspace = Boolean(useAuthStore.getState().claims?.tenant_id);
    await endCreatedSession(created.accessToken);
    await signOut();
    useAuthStore
      .getState()
      .endSession(
        hadWorkspace
          ? SESSION_MESSAGES.workspaceCreatedMultiple
          : SESSION_MESSAGES.workspaceCreated,
      );
    navigate("/login", { replace: true });
  };

  /** Confirmed: the session for the new workspace, adopted as it is. */
  const enter = (result: Enrolled) => {
    useAuthStore
      .getState()
      .setSession(
        result.accessToken,
        result.claims,
        result.activeTenantCode || undefined,
      );
    navigate("/app/home", { replace: true });
  };

  const leave = async () => {
    setSigningOut(true);
    await signOut();
    navigate("/login", { replace: true });
  };

  // Why no country can be chosen outranks "choose a country": it is the
  // reason, and Try again is the way out.
  const countryError = countries.error
    ? `${t("auth.workspace.countriesFailed")} ${errorMessage(countries.error, t, language)}`
    : !countries.isLoading && countries.options.length === 0
      ? t("auth.workspace.noCountries")
      : errors.country;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ inlineSize: 560, maxInlineSize: "100%" }}>
        <Card>
          {enrolFor ? (
            <FactorEnrolment enrollmentToken={enrolFor} onEnrolled={enter} />
          ) : (
            <>
              <PageHeader
                title={t("auth.workspace.title")}
                subtitle={t("auth.workspace.subtitle")}
              />
              <form onSubmit={submit} noValidate>
                <Field
                  id={IDS.name}
                  label={t("auth.workspace.name")}
                  required
                  error={errors.name}
                >
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setErrors((x) => ({ ...x, name: undefined }));
                    }}
                    required
                    maxLength={NAME_LENGTH.max}
                    autoComplete="organization"
                  />
                </Field>
                <Field
                  id={IDS.code}
                  label={t("auth.workspace.code")}
                  required
                  hint={t("auth.workspace.codeHint")}
                  error={errors.code}
                >
                  <Input
                    value={code}
                    onChange={(e) => {
                      setCode(
                        e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                      );
                      setErrors((x) => ({ ...x, code: undefined }));
                    }}
                    required
                    maxLength={CODE_LENGTH.max}
                    dir="ltr"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                </Field>
                <Field
                  id={IDS.country}
                  label={t("auth.workspace.country")}
                  required
                  hint={
                    countries.isLoading
                      ? t("auth.workspace.countriesLoading")
                      : t("auth.workspace.countryHint")
                  }
                  error={countryError}
                >
                  <Select
                    value={country}
                    onChange={(e) => chooseCountry(e.target.value)}
                    disabled={
                      countries.isLoading || countries.options.length === 0
                    }
                    required
                  >
                    <option value="" disabled>
                      {t("auth.workspace.chooseCountry")}
                    </option>
                    {countries.options.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                {countries.error && (
                  <div style={{ marginBlockEnd: 12 }}>
                    <Button size="sm" onClick={countries.retry}>
                      {t("common.tryAgain")}
                    </Button>
                  </div>
                )}
                <div className="atlas-grid atlas-grid--2">
                  <Field
                    id={IDS.currency}
                    label={t("auth.workspace.currency")}
                    required
                    hint={t("auth.workspace.currencyHint")}
                    error={errors.currency}
                  >
                    <Select
                      value={currency.value}
                      onChange={(e) => {
                        setCurrency({ value: e.target.value, byHand: true });
                        setErrors((x) => ({ ...x, currency: undefined }));
                      }}
                      required
                    >
                      <option value="" disabled>
                        {t("auth.workspace.chooseCurrency")}
                      </option>
                      {currencies.map((c) => {
                        // A runtime that has no name for a code (Chrome has none
                        // for SLE, ZWG, XCG) gives the code back: show it once.
                        const named = currencyName(c, language);
                        return (
                          <option key={c} value={c}>
                            {named === c ? c : `${c} — ${named}`}
                          </option>
                        );
                      })}
                    </Select>
                  </Field>
                  <Field
                    id={IDS.timezone}
                    label={t("auth.workspace.timezone")}
                    required
                    hint={t("auth.workspace.timezoneHint")}
                    error={errors.timezone}
                  >
                    <Select
                      value={timezone.value}
                      onChange={(e) => {
                        setTimezone({ value: e.target.value, byHand: true });
                        setErrors((x) => ({ ...x, timezone: undefined }));
                      }}
                      required
                    >
                      <option value="" disabled>
                        {t("auth.workspace.chooseTimezone")}
                      </option>
                      {/* Zone names are Latin: isolated left-to-right (LRI…PDI)
                      so "Africa/Cairo (UTC+03:00)" keeps its parenthesis in
                      place on a right-to-left screen, while the select keeps
                      the screen's direction. */}
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {`⁦${tz.label}⁩`}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
                <Field
                  id={IDS.language}
                  label={t("auth.workspace.language")}
                  required
                  hint={t("auth.workspace.languageHint")}
                  error={errors.language}
                >
                  <Select
                    value={locale.value}
                    onChange={(e) => {
                      setLocale({ value: e.target.value, byHand: true });
                      setErrors((x) => ({ ...x, language: undefined }));
                    }}
                    required
                  >
                    <option value="" disabled>
                      {t("auth.workspace.chooseLanguage")}
                    </option>
                    {languages.map((tag) => (
                      <option key={tag} value={tag} lang={tag}>
                        {languageName(tag)}
                      </option>
                    ))}
                  </Select>
                </Field>
                {submitError && (
                  <div
                    role="alert"
                    style={{
                      color: "var(--atlas-danger)",
                      fontSize: 13,
                      marginBlockEnd: 12,
                    }}
                  >
                    {submitError}
                  </div>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || signingOut}
                  style={{ inlineSize: "100%" }}
                >
                  {loading
                    ? t("auth.workspace.submitting")
                    : t("auth.workspace.submit")}
                </Button>
              </form>
            </>
          )}
          <div style={{ marginBlockStart: 16, textAlign: "center" }}>
            <Button
              variant="ghost"
              size="sm"
              disabled={loading || signingOut}
              onClick={() => void leave()}
            >
              {signingOut ? t("session.signingOut") : t("session.signOut")}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
